import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
// components
import ContentItem from "./Components/ContentItem";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
// types
import { PostStateData } from "@super-ui/components/m2c-swiggy-black-halfcard/types";
// styles
import {
    HeaderContainer,
    InfoContainer,
    Progress,
    ProgressBarContainer,
    InfoContent,
    PostLayoutContainer,
    ContentContainer,
    Title,
    ProgressBarWrapper,
    StarImage,
    PostLayoutWrapper,
    CtaContainer,
    LogoContainer,
} from "./styles";
// constants
import {
    M2C_HALFCARD_TEST_IDS,
    DEFAULT_PROGRESS_DURATION,
    THOUSAND,
    DELAY_FOR_PROGRESS_BAR,
    DELAY_FOR_DIVIDER_POPUP,
    DELAY_FOR_DIVIDER_HALFCARD,
    GRATIFICATION_POPUP_LOGO,
} from "@super-ui/components/m2c-swiggy-black-halfcard/constants";
import { GRATIFICATION_CLOSE_BUTTON } from "@super-ui/components/swiggy-one-widget-dls-wrapper/constants";
import { BlackBaseTheme } from "@super-ui/components/m2c-swiggy-black-halfcard/constants/theme";
import { STAR_IMAGE } from "../../constants";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { LottieAnimation, fetchLottie, useEffectOnce } from "@swiggy-private/core-ui";
// contexts
import { M2CHalfcardContext } from "@super-ui/contexts";

interface PostStateLayoutProps extends PostStateData {
    isPopup?: boolean;
}

const PostStateLayout: FC<PostStateLayoutProps> = ({
    duration = DEFAULT_PROGRESS_DURATION,
    logoCtx,
    title,
    closeBottomSheet,
    content,
    cta,
    isPopup = false,
}) => {
    const [showProgressBar, setShowProgressBar] = useState(isPopup);
    const [showDivider, setShowDivider] = useState(false);
    const {
        analyticsEvents: { onImpressionM2CHalfcardGratification },
    } = useContext(M2CHalfcardContext);

    const [m2cLottieData, setM2CLottieData] = useState(null);
    const [m2cLottieFailed, setM2CLottieFailed] = useState(false);

    useEffect(() => {
        if (!isEmpty(logoCtx?.lottie) && isEmpty(m2cLottieData) && !m2cLottieFailed) {
            fetchLottie({
                lottieId: logoCtx?.lottie,
                setLottieData: setM2CLottieData,
                setLottieFailed: setM2CLottieFailed,
            });
        }
    }, [m2cLottieData, m2cLottieFailed, logoCtx?.lottie]);

    const ctaText = useMemo(() => cta?.text, [cta]);

    useEffectOnce(() => {
        onImpressionM2CHalfcardGratification(title, JSON.stringify(content));
    });

    useEffect(() => {
        const totalDuration = isPopup ? duration : duration + DELAY_FOR_PROGRESS_BAR;
        const timer = setTimeout(() => {
            closeBottomSheet?.(GRATIFICATION_CLOSE_BUTTON.AUTO_COLLAPSE);
        }, totalDuration * THOUSAND);

        return () => clearTimeout(timer);
    }, [logoCtx, title, duration, closeBottomSheet, isPopup]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowProgressBar(true);
        }, DELAY_FOR_PROGRESS_BAR * THOUSAND);

        return () => clearTimeout(timer);
    }, [duration]);

    useEffect(() => {
        const duration = isPopup ? DELAY_FOR_DIVIDER_POPUP : DELAY_FOR_DIVIDER_HALFCARD;
        const timer = setTimeout(() => {
            setShowDivider(true);
        }, duration * THOUSAND);
        return () => clearTimeout(timer);
    }, [isPopup]);

    const onClose = useCallback(() => closeBottomSheet?.(GRATIFICATION_CLOSE_BUTTON.CTA_CLICK), [closeBottomSheet]);

    return (
        <ThemeProvider theme={BlackBaseTheme} colorMode="dark">
            <PostLayoutWrapper isPopup={isPopup}>
                <PostLayoutContainer isPopup={isPopup}>
                    <HeaderContainer data-testid={M2C_HALFCARD_TEST_IDS.GRATIFICATION_HEADER} isPopup={isPopup}>
                        {!isEmpty(m2cLottieData) && (
                            <LottieAnimation
                                loop={false}
                                visible
                                playIfVisible={false}
                                forwardSpeed={1}
                                backwardSpeed={1}
                                playLottie={true}
                                animationData={m2cLottieData}
                            />
                        )}
                        {isPopup && !isEmpty(logoCtx?.logo) && (
                            <>
                                <LogoContainer>
                                    <Image cloudinaryId={logoCtx?.logo} {...GRATIFICATION_POPUP_LOGO} />
                                </LogoContainer>
                                {!isEmpty(title) && (
                                    <Title variant="V2.subheading1.neutral.medium" color="primary">
                                        {title}
                                    </Title>
                                )}
                            </>
                        )}
                    </HeaderContainer>

                    <InfoContainer>
                        <InfoContent>
                            <>
                                {isPopup && <StarImage cloudinaryId={STAR_IMAGE} />}
                                {!isEmpty(content) && (
                                    <ContentContainer
                                        isPopup={isPopup}
                                        showDivider={showDivider}
                                        data-testid={M2C_HALFCARD_TEST_IDS.GRATIFICATION_CONTENT}
                                    >
                                        {content.length > 0 &&
                                            content.map((content, index) => (
                                                <ContentItem key={index} {...content} index={index} isPopup={isPopup} />
                                            ))}
                                    </ContentContainer>
                                )}

                                {!isEmpty(ctaText) && (
                                    <CtaContainer>
                                        <Text
                                            variant="V2.subheading1.neutral.medium"
                                            color="background_Button_Tertiary"
                                            onClick={onClose}
                                        >
                                            {ctaText}
                                        </Text>
                                    </CtaContainer>
                                )}
                            </>
                        </InfoContent>
                    </InfoContainer>

                    {showProgressBar && (
                        <ProgressBarWrapper>
                            <ProgressBarContainer isPopup={isPopup}>
                                <Progress duration={duration} isPopup={isPopup} />
                            </ProgressBarContainer>
                        </ProgressBarWrapper>
                    )}
                </PostLayoutContainer>
            </PostLayoutWrapper>
        </ThemeProvider>
    );
};

export default memo(PostStateLayout);
